import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTrustedSenders, addTrustedSender, removeTrustedSender, getSkippedSenders, trustSkipped, dismissSkipped, getSettings, updateSettings } from "@/lib/apiClient";
import { useApp } from "@/context/AppContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ShieldCheck, Plus, X, Loader2, MailX, Check, Globe, AtSign, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const SkippedRow = ({ item, onTrust, onDismiss, busy }) => (
  <li className="flex flex-col sm:flex-row sm:items-center gap-2 py-2.5" data-testid={`skipped-row-${item.message_id}`}>
    <div className="flex-1 min-w-0">
      <div className="text-sm truncate" data-testid="skipped-from">{item.from || item.from_email}{item.source === "inbound" && <span className="ml-2 rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] text-rose-400" data-testid="skipped-source-inbound">Forwarded</span>}</div>
      <div className="text-xs text-muted-foreground truncate">
        {item.subject || "(no subject)"}
        {item.attachments?.length > 0 && <span className="ml-2 inline-flex items-center gap-1"><Paperclip className="h-3 w-3" />{item.attachments.length}</span>}
        {item.received_at && <span className="ml-2">{new Date(item.received_at).toLocaleDateString()}</span>}
      </div>
    </div>
    <div className="flex flex-wrap items-center gap-1.5 shrink-0">
      <Button data-testid="skipped-trust-domain" size="sm" variant="outline" disabled={!!busy} onClick={() => onTrust(item, "domain")} title={`Trust everyone @${item.from_domain}`}>
        {busy === item.message_id + "domain" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Globe className="h-3.5 w-3.5 mr-1" />} Trust @{item.from_domain}
      </Button>
      <Button data-testid="skipped-trust-email" size="sm" variant="ghost" disabled={!!busy} onClick={() => onTrust(item, "email")} title="Trust this exact address only">
        {busy === item.message_id + "email" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <AtSign className="h-3.5 w-3.5" />}
      </Button>
      <button data-testid="skipped-dismiss" className="text-muted-foreground hover:text-foreground p-1" onClick={() => onDismiss(item)} title="Dismiss"><X className="h-4 w-4" /></button>
    </div>
  </li>
);

export const TrustedSenders = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [skipped, setSkipped] = useState({ items: [], total: 0 });
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(null);

  const load = () => Promise.all([getTrustedSenders().then(setData), getSkippedSenders().then(setSkipped)]);
  useEffect(() => { load(); }, []);

  if (!data) return null;

  const toggle = async (enabled) => {
    const s = await getSettings();
    await updateSettings({ ...s, sender_whitelist_enabled: enabled });
    setData({ ...data, enabled });
    toast.success(enabled ? "Only trusted senders will be ingested" : "Allow-list off — every matching email is ingested");
  };

  const add = async () => {
    if (!value.trim()) return;
    setBusy("add");
    try {
      await addTrustedSender(value.trim(), user);
      setValue("");
      toast.success(`Trusted ${value.trim().toLowerCase()}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add sender");
    }
    setBusy(null);
  };

  const remove = async (s) => {
    await removeTrustedSender(s.id, user);
    toast.success(`Removed ${s.value}`);
    load();
  };

  const trust = async (item, scope) => {
    setBusy(item.message_id + scope);
    try {
      const res = await trustSkipped(item.message_id, scope, user);
      if (res.created?.length) {
        toast.success(`Trusted ${res.trusted} — ${res.created.length} invoice(s) ingested`, {
          action: { label: "Open", onClick: () => navigate(`/invoice/${res.created[0].id}`) },
        });
      } else {
        toast.success(`Trusted ${res.trusted}` + (res.error ? ` — ${res.error}` : " — no document could be extracted from that email"));
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not trust sender");
    }
    setBusy(null);
  };

  const dismiss = async (item) => {
    await dismissSkipped(item.message_id);
    load();
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="trusted-senders-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-500">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div>
            <div className="font-medium">Trusted senders</div>
            <p className="text-sm text-muted-foreground mt-0.5">
              Only emails from these addresses or domains become invoices. Anything else is labelled{" "}
              <span className="font-mono text-foreground">D20/Skipped</span> and listed below so you can trust it in one click.
              {data.trust_supplier_contacts && " Supplier emails saved on the Suppliers page are trusted automatically."}
            </p>
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          Allow-list
          <Switch data-testid="whitelist-toggle" checked={data.enabled} onCheckedChange={toggle} />
        </label>
      </div>

      <div className="mt-4 flex gap-2">
        <Input data-testid="trusted-sender-input" placeholder="billing@acme.com or acme.com" value={value}
               onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} className="font-mono text-sm" />
        <Button data-testid="trusted-sender-add" onClick={add} disabled={busy === "add" || !value.trim()}>
          {busy === "add" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} Trust
        </Button>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5" data-testid="trusted-sender-list">
        {data.senders.length === 0 && data.supplier_contacts.length === 0 && (
          <span className="text-xs text-muted-foreground" data-testid="trusted-empty">No trusted senders yet — while the allow-list is on, every email will be skipped until you add one.</span>
        )}
        {data.senders.map((s) => (
          <span key={s.id} data-testid={`trusted-chip-${s.value}`} className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs font-mono" title={s.note || ""}>
            {s.kind === "domain" ? <Globe className="h-3 w-3 text-muted-foreground" /> : <AtSign className="h-3 w-3 text-muted-foreground" />}
            {s.value}
            <button data-testid={`trusted-remove-${s.value}`} onClick={() => remove(s)} className="text-muted-foreground hover:text-red-500 ml-0.5"><X className="h-3 w-3" /></button>
          </span>
        ))}
        {data.supplier_contacts.map((c) => (
          <span key={c.value} className="inline-flex items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-2 py-1 text-xs font-mono" title={`Supplier contact: ${c.supplier}`}>
            <Check className="h-3 w-3 text-emerald-500" /> {c.value}
          </span>
        ))}
      </div>

      <div className="mt-5 border-t border-border pt-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <MailX className="h-4 w-4 text-amber-500" /> Skipped senders
          <span className={cn("rounded-md px-1.5 py-0.5 text-[11px] font-mono", skipped.total ? "bg-amber-500/15 text-amber-500" : "bg-muted text-muted-foreground")} data-testid="skipped-count">{skipped.total}</span>
        </div>
        {skipped.items.length === 0 ? (
          <p className="text-xs text-muted-foreground mt-2" data-testid="skipped-empty">Nothing skipped — every recent email came from a trusted sender.</p>
        ) : (
          <ul className="divide-y divide-border mt-1" data-testid="skipped-list">
            {skipped.items.map((it) => <SkippedRow key={it.message_id} item={it} onTrust={trust} onDismiss={dismiss} busy={busy} />)}
          </ul>
        )}
      </div>
    </div>
  );
};
